import { useUpdateVolunteerSlot } from '@sola/my-sdk/volunteers/hooks.ts';
import type { GraphQLVolunteerSlot } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { VolunteerSlotStatusEnum } from '@sola/schema/dotcom/supporters/volunteers.ts';
import {
  type BaseVolunteerEventViewModel,
  type VolunteerSlotContact,
  createVolunteerSlotStatusUpdates,
  getVolunteerSlotContacts,
  groupVolunteerSlots,
  isVolunteerSlotCancellable,
  sortVolunteerEvents,
} from '@sola/shared-utils/supporters/volunteers.ts';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks/base.ts';
import {
  supporterContactsQuery,
  volunteerEventsQuery,
} from '@sola/supporter-sdk/queries.ts';

import { useLocalAlerts } from '../../molecules/local-alerts/local-alerts-provider.tsx';
import {
  createVolunteerActionSuccessAlert,
  createVolunteerActionWarningAlert,
} from '../../utils/alerts.tsx';

export type VolunteerEventViewModel = BaseVolunteerEventViewModel & {
  cancellableSlots: GraphQLVolunteerSlot[];
  contacts: VolunteerSlotContact[];
};

export const getCancellableVolunteerSlots = (slots: GraphQLVolunteerSlot[]) =>
  slots.filter(({ status }) => isVolunteerSlotCancellable(status));

export const useVolunteerEventsLogic = () => {
  const { mutateAsync: updateSlot } = useUpdateVolunteerSlot();
  const { addAlerts, clearAlerts } = useLocalAlerts();
  const {
    data: [volunteerEvents, contacts],
    isPending,
    isError,
  } = useSupporterQuery([volunteerEventsQuery, supporterContactsQuery]);
  const supporterContacts = contacts || [];
  const volunteerEventViewModels: VolunteerEventViewModel[] =
    sortVolunteerEvents(volunteerEvents || []).map((event) => {
      const { locationName, slots } = event;
      const cancellableSlots = getCancellableVolunteerSlots(slots);

      return {
        ...event,
        cancellableSlots,
        contacts: getVolunteerSlotContacts(
          cancellableSlots.map(({ contactId }) => contactId),
          supporterContacts,
        ),
        slotGroups: groupVolunteerSlots(slots, locationName).map(
          ({ contactIds, ...slotGroup }) => ({
            ...slotGroup,
            contacts: getVolunteerSlotContacts(contactIds, supporterContacts),
          }),
        ),
      };
    });

  const handleCancelSlot = async (slots: GraphQLVolunteerSlot[]) => {
    const { failedUpdates } = await updateSlot(
      createVolunteerSlotStatusUpdates(
        slots,
        VolunteerSlotStatusEnum.CanceledByVolunteer,
      ),
    );

    clearAlerts();
    addAlerts(
      failedUpdates.length
        ? createVolunteerActionWarningAlert('cancel', slots.length)
        : createVolunteerActionSuccessAlert('cancel', slots.length),
    );
  };

  const handleConfirmSlot = async (slots: GraphQLVolunteerSlot[]) => {
    const { failedUpdates } = await updateSlot(
      createVolunteerSlotStatusUpdates(
        slots,
        VolunteerSlotStatusEnum.Confirmed,
      ),
    );

    clearAlerts();
    addAlerts(
      failedUpdates.length
        ? createVolunteerActionWarningAlert('confirm', slots.length)
        : createVolunteerActionSuccessAlert('confirm', slots.length),
    );
  };

  return {
    handleCancelSlot,
    handleConfirmSlot,
    isError,
    isPending,
    volunteerEvents: volunteerEventViewModels,
  };
};
